import { Injectable, inject } from '@angular/core';
import { NtsStateManagementService } from '@ntersol/state-management';
import { BehaviorSubject, Observable, debounceTime, distinctUntilChanged, filter, forkJoin, iif, map, switchMap, take, tap } from 'rxjs';
import { HousesApiStoreService, SearchType } from '../../routes/houses/shared';
import { SearchStoreService, filtersChanged } from '../../shared/stores';
import { ApiService } from '../../shared/stores/api';
import { listenForLargeScreenDown } from '../../shared/utils';

export interface DisplayValue {
  label: string | undefined;
  value: number | undefined;
}
export interface SelectedOptions {
  selectedAuctionTypes: (number | undefined)[];
  selectedPropertyTypes: (number | undefined)[];
}

@Injectable({
  providedIn: 'root',
})
export class ToolbarService {
  private readonly api = inject(ApiService);
  private readonly searchStore = inject(SearchStoreService);
  private readonly sm = inject(NtsStateManagementService);
  private readonly housesApiStoreService = inject(HousesApiStoreService);
  readonly propertySearchForm = this.searchStore.propertySearchForm;

  readonly auctionTypes$: Observable<DisplayValue[]> = this.api.salesTypes.selectAll$.pipe(
    filter(types => !!types),
    map(types => (types || []).map(type => ({ label: type.CATEGORYNAME, value: type.CATEGORYID }))),
  );

  readonly propertyTypes$: Observable<DisplayValue[]> = this.api.propertyType.selectAll$.pipe(
    filter(types => !!types),
    map(list => list?.map(type => ({ label: type.TYPE_CAT_DESC, value: type.PROP_TYPE_CAT_ID })) || []),
  );

  public readonly largeScreenDownObserver$ = listenForLargeScreenDown();

  private readonly _filtersCanBeApplied$ = new BehaviorSubject(false);
  public readonly filtersCanBeApplied$ = this._filtersCanBeApplied$.asObservable();

/** Tracks if user has pending filter changes that haven't been applied yet */
  private _hasPendingChanges = false;


  dispatchFilterChanges() {
    return this.largeScreenDownObserver$.pipe(
      distinctUntilChanged(),
      switchMap(isScreenSizeLessThanLarge => {
        return iif(() => isScreenSizeLessThanLarge, this.getPropertyFiltersMobileObserver(), this.getPropertyFiltersDesktopObserver());
      }),
      tap(() => {
        this.housesApiStoreService.stopSurroundedArea();
        this.housesApiStoreService.setSearchType(SearchType.filter);
        this.sm.dispatch(filtersChanged(this.propertySearchForm.value));
      }),
    );
  }

  getSelectedOptions() {
    return forkJoin({
      queryParamsFilters: this.searchStore.queryParamsFilters$.pipe(take(1)),
      auctionTypes: this.auctionTypes$.pipe(take(1)),
      propertyTypes: this.propertyTypes$.pipe(take(1)),
    }).pipe(
      tap(response => {
        // Don't overwrite form if user has pending changes that weren't applied
        if (this._hasPendingChanges) {
          return;
        }

        const queryParamsFilters = response.queryParamsFilters;
        const hasParams = Object.values(queryParamsFilters!)
          .filter(Boolean)
          .filter(value => Object.keys(value).length > 0);
        if (hasParams.length > 0) {
          this.propertySearchForm.reset(undefined, { emitEvent: false });
          // If TYPE is not defined in query params, load all types from API
          const filtersWithType = {
            ...response.queryParamsFilters,
            TYPE: response.queryParamsFilters?.TYPE ?? response.propertyTypes.map(type => type.value),
          };
          this.propertySearchForm.patchValue(filtersWithType);
        } else {
          this.propertySearchForm.patchValue({
            CATEGORYLIST: response.auctionTypes.map(type => type.value),
            TYPE: response.propertyTypes.map(type => type.value),
          });
        }
      }),
    );
  }

  markPendingChanges() {
    this._hasPendingChanges = true;
  }

  clearPendingChanges() {
    this._hasPendingChanges = false;
  }

  hasPendingChanges() {
    return this._hasPendingChanges;
  }

  syncFormWithUrlIfNoPendingChanges() {
    if (!this._hasPendingChanges) {
      this.getSelectedOptions().subscribe();
    }
  }

  allowApplyFilter() {
    this.housesApiStoreService.stopSurroundedArea();
    this._hasPendingChanges = false;
    this._filtersCanBeApplied$.next(true);
  }

  ensureDefaultFiltersInUrl() {
    return this.searchStore.queryParams$.pipe(
      map(params => !!params?.filters),
      distinctUntilChanged(),
      tap(hasFiltersInUrl => {
        if (!hasFiltersInUrl) {
          this.sm.dispatch(filtersChanged(this.propertySearchForm.value));
        }
      }),
    );
  }

  private getPropertyFiltersMobileObserver(): Observable<unknown> {
    return this.filtersCanBeApplied$.pipe(filter(canBeApplied => canBeApplied));
  }

  private getPropertyFiltersDesktopObserver(): Observable<unknown> {
    return this.propertySearchForm.valueChanges.pipe(debounceTime(500));
  }
}
